/**
 * Portal do Condômino (0.1.31) — home mobile-first de acesso direto.
 * Blocos grandes para o morador: Avisos e Profissionais (Fase 1). As próximas
 * fases (reservas, chamados, manutenções, notificações) entram como novos blocos.
 *
 * 0.1.33: bloco "Resumo do mês" — abre a tela de Resumo (imagem amigável do
 * último mês, com opção de trocar o mês e enviar direto no WhatsApp).
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";
import { ArrowLeft, Bell, BellRing, Building2, CalendarClock, FileBarChart, FileText, Gavel, Loader2, LogOut, Megaphone, MessageSquarePlus, Package, Phone, PhoneCall, ShieldCheck, Vote, Wrench, type LucideIcon } from "lucide-react";
import { api } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";
import { ativarNotificacoes, desativarNotificacoes, enviarTeste, estaAtivo, sincronizarInscricao, suportaPush } from "../lib/push";
import { WhatsappPortal } from "./WhatsappPortal";

interface Cond { nome: string }


/** Celular/tablet? Notificações são oferecidas só no celular (no desktop o serviço
 *  de push depende de rede/perfil e confunde o usuário comum). */
function ehCelular(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const porUA = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(ua);
  const porToque = (navigator.maxTouchPoints || 0) > 1;
  return porUA || porToque;
}

/** Guia ilustrado (PDF de gerar_guia_notificacoes.py, servido pelo próprio app). */
const GUIA_URL = "/guias/Guia-Notificacoes-Celular.pdf";

/** 0.1.79: janela "de dentro" de outro app (WhatsApp, Instagram, Facebook...). Nela não
 *  dá para instalar nem ativar notificações — é o motivo nº 1 de "não funciona".
 *  (No iPhone o WhatsApp pode abrir a janela do Safari, que tem o mesmo UA; aí cai no
 *  passo a passo do iPhone abaixo, que também resolve.) */
function navegadorInterno(): string | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  if (/WhatsApp/i.test(ua)) return "WhatsApp";
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return "Facebook";
  if (/Line\//i.test(ua)) return "Line";
  if (/Android/i.test(ua) && /; wv\)/i.test(ua)) return "outro aplicativo";
  return null;
}

/** iPhone/iPad (o iPadOS se apresenta como Mac com toque). */
function ehIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1);
}

/** Aberto pelo ícone da tela de início (modo app)? */
function emModoApp(): boolean {
  if (typeof window === "undefined") return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia?.("(display-mode: standalone)").matches === true;
}

/** Versão do iOS pelo UA, ex.: { maior: 16, menor: 3 }. Push no iPhone exige 16.4+. */
function versaoIOS(): { maior: number; menor: number } | null {
  const m = /OS (\d+)_(\d+)/.exec(navigator.userAgent || "");
  return m ? { maior: Number(m[1]), menor: Number(m[2]) } : null;
}

function LinkGuia() {
  return (
    <a href={GUIA_URL} target="_blank" rel="noopener" className="underline font-medium">
      guia com figuras
    </a>
  );
}

function AtivarNotificacoes({ condominioId }: { condominioId: number }) {
  const [suporta] = useState(suportaPush());
  const [celular] = useState(ehCelular());
  const [interno] = useState(navegadorInterno());
  const [teste, setTeste] = useState<{ ok: boolean; msg: string } | null>(null);
  const [ativo, setAtivo] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    estaAtivo().then((a) => {
      setAtivo(a);
      // Se já está ativo, reenvia a inscrição ATUAL ao servidor (auto-cura o
      // endpoint que muda quando o app é atualizado/reinstalado).
      if (a) void sincronizarInscricao(condominioId);
    }).catch(() => setAtivo(false));
  }, [condominioId]);

  if (interno) {
    return (
      <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2">
        <Bell size={18} className="flex-shrink-0 mt-0.5" />
        <div>Você abriu o Portal por dentro do <b>{interno}</b>, onde as notificações não
          funcionam. Toque nos <b>três pontinhos</b> (ou em compartilhar) desta janela e escolha{" "}
          <b>{ehIOS() ? "Abrir no Safari" : "Abrir no Chrome"}</b>. Veja o <LinkGuia />.</div>
      </div>
    );
  }

  if (ehIOS() && !emModoApp()) {
    // iPhone: Web Push só existe no app instalado na Tela de Início (iOS 16.4+).
    const v = versaoIOS();
    if (v && (v.maior < 16 || (v.maior === 16 && v.menor < 4))) {
      return (
        <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2">
          <Bell size={18} className="flex-shrink-0 mt-0.5" />
          <div>Este iPhone está com o <b>iOS {v.maior}.{v.menor}</b>; as notificações precisam do{" "}
            <b>iOS 16.4 ou mais novo</b> (Ajustes &gt; Geral &gt; Atualização de Software). Enquanto
            isso, os avisos continuam chegando por <b>e-mail</b>.</div>
        </div>
      );
    }
    return (
      <div className="mb-4 rounded-xl bg-brand-50 border border-brand-200 p-3 text-sm text-brand-900">
        <div className="flex gap-2 font-medium">
          <Bell size={18} className="flex-shrink-0 mt-0.5 text-brand-700" />
          Para receber avisos no iPhone, instale o Portal (1 minuto):
        </div>
        <ol className="mt-1.5 ml-7 list-decimal space-y-0.5 text-[13px]">
          <li>No <b>Safari</b>, toque em <b>Compartilhar</b> (quadrado com seta, na barra de <b>baixo</b>;
            no iOS 26 fica dentro do botão <b>...</b>).</li>
          <li><b>Role a lista para baixo</b> e toque em <b>Adicionar à Tela de Início</b>.</li>
          <li>Toque em <b>Adicionar</b> (canto de cima, à direita).</li>
          <li>Abra o Portal <b>pelo ícone novo</b> e toque em <b>Ativar</b>.</li>
        </ol>
        <p className="mt-1.5 ml-7 text-xs text-brand-800">Dúvidas? Veja o <LinkGuia />.</p>
      </div>
    );
  }

  if (!suporta) {
    return (
      <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2">
        <Bell size={18} className="flex-shrink-0 mt-0.5" />
        <div>Este navegador não recebe <b>notificações</b>. No Android, abra o Portal pelo{" "}
          <b>Chrome</b> e instale-o na tela inicial. Veja o <LinkGuia />.</div>
      </div>
    );
  }

  // No COMPUTADOR não oferecemos ativar (o push do desktop depende de rede/perfil e
  // confunde). Só mostramos o aviso — a menos que já esteja ativo neste aparelho,
  // caso em que deixamos desativar. As notificações são um recurso do celular.
  if (!celular && !ativo) {
    return (
      <div className="mb-4 rounded-xl bg-brand-50 border border-brand-200 p-3 text-sm text-brand-900 flex gap-2">
        <Bell size={18} className="flex-shrink-0 mt-0.5 text-brand-700" />
        <div>As <b>notificações</b> (avisos e votações) são ativadas pelo <b>celular</b>.
          Abra este endereço no celular e toque em <b>Ativar</b> — de preferência após
          adicionar o app à tela de início.</div>
      </div>
    );
  }

  async function ligar() {
    setOcupado(true); setErro(null);
    try { await ativarNotificacoes(condominioId); setAtivo(true); }
    catch (e) {
      const m = (e as Error).message || "";
      let msg: string;
      if (m === "permissao-negada") {
        msg = "Você bloqueou as notificações no navegador. Libere nas configurações do site (cadeado → Notificações → Permitir).";
      } else if (m === "nao-suportado") {
        msg = "Este navegador não suporta notificações. No iPhone, primeiro adicione o site à Tela de Início e abra por lá.";
      } else if (m === "sem-chave-no-servidor") {
        msg = "O servidor não devolveu a chave de notificações. Avise o administrador.";
      } else if (m.startsWith("falha-inscricao:")) {
        msg = "O navegador recusou a inscrição de notificações (pode ser bloqueio de rede ao serviço de push). Detalhe: " + m.replace("falha-inscricao:", "");
      } else {
        msg = "Não foi possível ativar as notificações agora. Detalhe: " + m;
      }
      setErro(msg);
    } finally { setOcupado(false); }
  }
  async function desligar() {
    setOcupado(true); setTeste(null);
    try { await desativarNotificacoes(); setAtivo(false); } finally { setOcupado(false); }
  }
  // 0.1.79: o próprio morador confere se chega (antes só dava para testar com um aviso real).
  async function testar() {
    setOcupado(true); setTeste(null);
    try {
      const res = await enviarTeste();
      setTeste(res.ok
        ? { ok: true, msg: "Enviado! A mensagem \"Teste de notificação\" deve aparecer em alguns segundos. Não apareceu? Confira o modo Foco/Não Perturbe e a economia de bateria no guia." }
        : { ok: false, msg: res.detalhe });
    } catch (e) {
      const m = (e as Error).message || "";
      setTeste({ ok: false, msg: m === "sem-inscricao"
        ? "Este aparelho não está mais inscrito. Toque em Desativar e depois em Ativar."
        : "Não foi possível enviar o teste agora. Detalhe: " + m });
    } finally { setOcupado(false); }
  }

  return (
    <div className="mb-4 rounded-xl bg-brand-50 border border-brand-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-brand-900 min-w-0">
          {ativo ? <BellRing size={18} className="text-brand-700 flex-shrink-0" /> : <Bell size={18} className="text-brand-700 flex-shrink-0" />}
          <span className="truncate">
            {ativo ? "Notificações ativas neste aparelho" : "Receba avisos e votações no celular (grátis)"}
          </span>
        </div>
        {ativo ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={testar} disabled={ocupado}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-brand-700 text-white disabled:opacity-50">
              {ocupado ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />} Testar notificação
            </button>
            <button onClick={desligar} disabled={ocupado}
              className="text-xs px-2.5 py-1.5 rounded-md border border-brand-300 text-brand-800 bg-white">Desativar</button>
          </div>
        ) : (
          <button onClick={ligar} disabled={ocupado}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-brand-700 text-white flex-shrink-0 disabled:opacity-50">
            {ocupado ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />} Ativar
          </button>
        )}
      </div>
      {erro && <p className="mt-1.5 text-xs text-rose-600">{erro}</p>}
      {teste && <p className={"mt-1.5 text-xs " + (teste.ok ? "text-emerald-700" : "text-rose-600")}>{teste.msg}</p>}
      {(erro || (teste && !teste.ok)) && <p className="mt-1 text-xs text-brand-800">Veja o <LinkGuia />.</p>}
    </div>
  );
}

// Acentos neon (mesma paleta dos cards do app), ciclados por tile.
const PORTAL_ACCENTS: { hex: string; rgb: string }[] = [
  { hex: "#22C9B8", rgb: "34,201,184" },   // teal
  { hex: "#9684F6", rgb: "150,132,246" },  // violeta
  { hex: "#E8A13A", rgb: "232,161,58" },   // âmbar
  { hex: "#5A8CE6", rgb: "90,140,230" },   // azul
  { hex: "#34C484", rgb: "52,196,132" },   // verde
  { hex: "#E87BA4", rgb: "232,123,164" },  // rosa
];

function Bloco({
  icone: Icone, titulo, descricao, accentIndex = 0, onClick, href,
}: {
  icone: LucideIcon; titulo: string; descricao: string; accentIndex?: number;
  onClick?: () => void; href?: string;   // href = abre direto (ex.: PDF), sem 2º clique
}) {
  const acc = PORTAL_ACCENTS[accentIndex % PORTAL_ACCENTS.length];
  const cls =
    "group relative block w-full text-left rounded-2xl p-5 overflow-hidden border " +
    "transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.99]";
  const style: CSSProperties = {
    background:
      `radial-gradient(130% 85% at 50% -12%, rgba(${acc.rgb},0.26), transparent 55%),` +
      `linear-gradient(180deg,#17263f 0%,#0f1a2e 100%)`,
    borderColor: "rgba(255,255,255,0.10)",
    boxShadow: "0 14px 34px rgba(8,15,30,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
  };
  const conteudo = (
    <>
      {/* Filete neon colorido na borda superior */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl transition-all group-hover:h-[4px]"
        style={{ background: acc.hex, boxShadow: `0 0 9px 1px rgba(${acc.rgb},0.9), 0 0 20px 3px rgba(${acc.rgb},0.5)` }}
      />
      <div className="relative flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform"
          style={{ background: `rgba(${acc.rgb},0.14)`, border: `1px solid rgba(${acc.rgb},0.35)` }}
        >
          <Icone size={26} style={{ color: acc.hex }} />
        </div>
        <div>
          <div className="text-lg font-bold text-white">{titulo}</div>
          <div className="text-sm text-slate-300/80">{descricao}</div>
        </div>
      </div>
    </>
  );
  if (href) {
    return <a href={href} target="_blank" rel="noreferrer" className={cls} style={style}>{conteudo}</a>;
  }
  return <button onClick={onClick} className={cls} style={style}>{conteudo}</button>;
}

export function PortalMoradorPage({
  condominioId, condominios, onTrocarCondominio, usuarioNome, onAbrirAvisos, onAbrirProfissionais, onAbrirReservas, onAbrirChamados,
  onAbrirResumo, onAbrirManutencoes, onAbrirNotificacoes, onAbrirTelefones, onAbrirBoletos,
  onAbrirVotacoes, onAbrirEncomendas, onAbrirEncomendasGestao, onAbrirVisitas, onAbrirVisitasGestao, onSair, onVoltar,
}: {
  condominioId: number;
  condominios?: { id: number; nome: string }[];      // condomínios do usuário (p/ trocar)
  onTrocarCondominio?: (id: number) => void;          // troca o condomínio ativo do Portal
  usuarioNome?: string;                               // nome de quem está logado (mostra no topo)
  onAbrirAvisos: () => void;
  onAbrirProfissionais: () => void;
  onAbrirReservas: () => void;
  onAbrirChamados: () => void;
  onAbrirResumo: () => void;
  onAbrirManutencoes: () => void;
  onAbrirNotificacoes: () => void;
  onAbrirTelefones: () => void;
  onAbrirBoletos: () => void;
  onAbrirVotacoes: () => void;
  onAbrirEncomendas: () => void;                       // morador: consulta as próprias
  onAbrirEncomendasGestao?: () => void;                // porteiro/síndico: registrar + dar baixa
  onAbrirVisitas: () => void;                          // morador: convites + autorizados
  onAbrirVisitasGestao?: () => void;                   // porteiro/síndico: portaria (entrada/saída)
  onSair: () => void;
  onVoltar?: () => void;   // gestor/Dono em preview: volta ao painel (em vez de sair)
}) {
  const { data: cond } = useQuery<Cond>({
    queryKey: ["condominio-portal", condominioId],
    queryFn: () => api.get<Cond>(`/condominios/${condominioId}`),
    retry: false,   // morador não tem permissão nesta rota (403) — não insiste
  });
  // Nome do condomínio: prioriza a lista vinda do /me (o morador SEMPRE consegue ler),
  // porque o GET /condominios/{id} exige permissão que o condômino não tem (dá 403 e
  // deixaria o nome como "…").
  const nomeCondominio =
    condominios?.find((c) => c.id === condominioId)?.nome ?? cond?.nome ?? "…";

  // Logo opcional do condomínio (mostra num círculo no topo; senão, ícone padrão).
  const { data: logoData } = useQuery<{ logo: string | null }>({
    queryKey: ["logo-condominio", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/logo`),
    retry: false,
  });
  const logo = logoData?.logo ?? null;

  // O PORTEIRO (escreve encomendas, mas NÃO é gestor e não tem painel) registra/dá baixa
  // direto do Portal. O síndico/Dono veem só a consulta aqui — a gestão deles é pelo painel
  // do condomínio (Portal é só leitura para gestores, para ficar padronizado).
  const { podeEscrever, podeLer, ehSindico, isDono } = usePermissoes();
  // Blocos dinâmicos: cada um só aparece se o usuário TEM leitura da funcionalidade.
  const _l = (f: string) => podeLer(condominioId, f);
  // 0.1.83: o Resumo do mês (prestação de contas) é do proprietário; o inquilino só vê
  // se o condomínio liberar. Na dúvida (erro), mantém o bloco — o servidor também confere.
  const { data: minhaUn } = useQuery<{ ve_resumo: boolean }>({
    queryKey: ["minha-unidade", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/minha-unidade`),
  });
  const veResumo = minhaUn?.ve_resumo ?? true;
  const gereEncomendas =
    podeEscrever(condominioId, "encomendas") && !ehSindico(condominioId) && !isDono && !!onAbrirEncomendasGestao;
  const abrirEncomendas = gereEncomendas ? onAbrirEncomendasGestao! : onAbrirEncomendas;
  // Porteiro (escreve visitas, mas não é gestor) abre a tela da PORTARIA direto do Portal.
  const gereVisitas =
    podeEscrever(condominioId, "visitas") && !ehSindico(condominioId) && !isDono && !!onAbrirVisitasGestao;
  const abrirVisitas = gereVisitas ? onAbrirVisitasGestao! : onAbrirVisitas;

  return (
    <div className="max-w-lg mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <div className="flex items-center gap-2 min-w-0">
          {logo ? (
            <img src={logo} alt="Logo do condomínio"
              className="w-10 h-10 rounded-full object-cover ring-2 ring-brand-200 flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-brand-700 text-white flex items-center justify-center flex-shrink-0">
              <Building2 size={20} />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Portal do Condômino</div>
            {(condominios?.length ?? 0) > 1 && onTrocarCondominio ? (
              // Quem tem mais de um condomínio ESCOLHE onde quer trabalhar.
              <select
                value={condominioId}
                onChange={(e) => onTrocarCondominio(Number(e.target.value))}
                title="Trocar de condomínio"
                className="max-w-[70vw] text-lg font-bold text-slate-900 bg-transparent border border-slate-200 rounded-md px-1.5 py-0.5 -ml-1 truncate focus:outline-none focus:ring-2 focus:ring-brand-300"
              >
                {condominios!.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            ) : (
              <div className="text-lg font-bold text-slate-900 truncate">{nomeCondominio}</div>
            )}
            {usuarioNome && (
              <div className="text-xs text-slate-500 truncate">Olá, {usuarioNome}</div>
            )}
          </div>
        </div>
        {onVoltar ? (
          <button onClick={onVoltar} title="Voltar ao painel"
            className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Voltar</span>
          </button>
        ) : (
          <button onClick={onSair} title="Sair"
            className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
            <LogOut size={16} /> <span className="hidden sm:inline">Sair</span>
          </button>
        )}
      </div>

      <AtivarNotificacoes condominioId={condominioId} />
      <WhatsappPortal condominioId={condominioId} />

      <div className="space-y-3">
        {_l("boletos") && (
          <Bloco icone={FileText} titulo="DOC" descricao="Baixe a 2ª via do seu boleto"
            accentIndex={0} onClick={onAbrirBoletos} />
        )}
        {_l("encomendas") && (
          <Bloco icone={Package} titulo="Encomendas"
            descricao={gereEncomendas ? "Registrar chegada e dar baixa na retirada" : "Encomendas na portaria e código de retirada"}
            accentIndex={1} onClick={abrirEncomendas} />
        )}
        {_l("visitas") && (
          <Bloco icone={ShieldCheck} titulo={gereVisitas ? "Visitas (Portaria)" : "Visitas"}
            descricao={gereVisitas ? "Validar convite, entrada/saída e livro do dia" : "Convide visitantes (QR) e autorize quem chega"}
            accentIndex={11} onClick={abrirVisitas} />
        )}
        {_l("votacoes") && (
          <Bloco icone={Vote} titulo="Votações" descricao="Assembleias, votações e enquetes"
            accentIndex={2} onClick={onAbrirVotacoes} />
        )}
        {_l("avisos") && (
          <Bloco icone={Megaphone} titulo="Avisos" descricao="Comunicados do seu condomínio"
            accentIndex={3} onClick={onAbrirAvisos} />
        )}
        {_l("reservas") && (
          <Bloco icone={CalendarClock} titulo="Reservas" descricao="Salão, churrasqueira, quadra…"
            accentIndex={4} onClick={onAbrirReservas} />
        )}
        {_l("chamados") && (
          <Bloco icone={MessageSquarePlus} titulo="Sugestões / Reclamações" descricao="Fale com o síndico"
            accentIndex={5} onClick={onAbrirChamados} />
        )}
        {_l("manutencoes") && (
          <Bloco icone={Wrench} titulo="Manutenções" descricao="Calendário de manutenções do prédio"
            accentIndex={6} onClick={onAbrirManutencoes} />
        )}
        {_l("notificacoes") && (
          <Bloco icone={Gavel} titulo="Notificações" descricao="Notificações da sua unidade"
            accentIndex={7} onClick={onAbrirNotificacoes} />
        )}
        {/* Telefones de emergência: informação de segurança — visível a qualquer membro. */}
        <Bloco icone={PhoneCall} titulo="Telefones Emergenciais" descricao="Lista de emergência do condomínio"
          accentIndex={8} onClick={onAbrirTelefones} />
        {_l("profissionais") && (
          <Bloco icone={Phone} titulo="Profissionais" descricao="Contatos úteis (hidráulico, elétrica…)"
            accentIndex={9} onClick={onAbrirProfissionais} />
        )}
        {_l("balancete") && veResumo && (
          <Bloco icone={FileBarChart} titulo="Resumo do mês" descricao="Veja o resumo do balancete"
            accentIndex={10} onClick={onAbrirResumo} />
        )}
      </div>

      <p className="text-center text-[11px] text-slate-400 mt-6">
        Dica: no celular, use "Adicionar à tela inicial" para abrir como um app.
      </p>
    </div>
  );
}
